import React, { useState, useEffect, useRef } from "react";
import {
  getAllPosts,
  createPost,
  updatePost,
  deletePost,
} from "../data/supabasePosts";
import { BlogPost } from "../types/blog";
import { marked } from "marked";
import { uploadFile, BLOG_IMAGES_BUCKET } from "../utils/storage";
import { sendPostNotification } from "../utils/subscriptionUtils";
import { v4 as uuidv4 } from "uuid";
import { supabase } from "../utils/supabase";
import LoadingSpinner, { LoadingTable } from "./LoadingSpinner";
import SubscriberManager from "./SubscriberManager";
import SubstackSync from "./SubstackSync";
import { substackSync } from "../utils/substackSync";
import RichTextEditor from "./RichTextEditor";
import ContentTypeSelector from "./ContentTypeSelector";
import { EmbeddedImage } from "../types/blog";

const AdminPanel: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isSendingEmails, setIsSendingEmails] = useState(false);
  const [emailStatus, setEmailStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    title: "",
    author: "",
    content: "",
    excerpt: "",
    imageUrl: "",
    isDraft: true,
    contentType: "markdown" as "markdown" | "rich-text",
    embeddedImages: [] as EmbeddedImage[],
  });
  const [previewHtml, setPreviewHtml] = useState<string>("");
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [sendingEmailPostId, setSendingEmailPostId] = useState<string | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"posts" | "subscribers" | "substack">("posts");

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const allPosts = await getAllPosts();
      setPosts(allPosts);
    } catch (err) {
      console.error("Error loading posts:", err);
      setError("Failed to load posts");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectPost = (post: BlogPost) => {
    setSelectedPost(post);
    setFormData({
      title: post.title,
      author: post.author,
      content: post.rawContent || post.content || "",
      excerpt: post.excerpt || "",
      imageUrl: post.imageUrl || "",
      isDraft: post.isDraft !== false, // Default to true if undefined
      contentType: post.contentType || "markdown",
      embeddedImages: post.embeddedImages || [],
    });
    setIsEditing(true);
  };

  const handleNewPost = () => {
    setSelectedPost(null);
    setFormData({
      title: "",
      author: "Arvind KC",
      content: "",
      excerpt: "",
      imageUrl: "",
      isDraft: true,
      contentType: "markdown",
      embeddedImages: [],
    });
    setIsEditing(true);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Update preview when content changes
    if (name === "content") {
      updatePreview(value);
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
  };

  const handleContentTypeChange = (contentType: "markdown" | "rich-text") => {
    setFormData({ ...formData, contentType });
    
    // Clear preview when switching content types
    setPreviewHtml("");
    
    // Update preview based on new content type
    if (contentType === "markdown" && formData.content) {
      updatePreview(formData.content);
    }
  };

  const handleRichTextChange = (content: string, embeddedImages: EmbeddedImage[]) => {
    setFormData({ 
      ...formData, 
      content,
      embeddedImages
    });
    
    // For rich text, content is already HTML
    setPreviewHtml(content);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    setIsUploading(true);

    try {
      // Generate a unique filename based on timestamp and original name
      const timestamp = new Date().getTime();
      const fileExtension = file.name.split(".").pop() || "";
      const sanitizedTitle = formData.title
        ? formData.title
            .toLowerCase()
            .replace(/[^\w\s]/gi, "")
            .replace(/\s+/g, "-")
        : "untitled";
      const fileName = `${timestamp}-${sanitizedTitle}.${fileExtension}`;

      // Upload to Supabase storage
      const filePath = `posts/${fileName}`;
      const imageUrl = await uploadFile(file, filePath);

      if (imageUrl) {
        setFormData({
          ...formData,
          imageUrl,
        });
      } else {
        alert("Failed to upload image. Please try again.");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Error uploading image. Please try again.");
    } finally {
      setIsUploading(false);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handlePublishPost = async () => {
    setIsLoading(true);
    console.log("Publishing post...");

    try {
      if (selectedPost) {
        console.log("Selected post before update:", selectedPost);

        // Create a clean update object with only the necessary fields
        const updateData = {
          id: selectedPost.id,
          title: selectedPost.title,
          author: selectedPost.author,
          content: selectedPost.content,
          rawContent: selectedPost.rawContent,
          excerpt: selectedPost.excerpt || "",
          imageUrl: selectedPost.imageUrl || "",
          date: selectedPost.date,
          likes: selectedPost.likes || 0,
          isDraft: false, // Set to false to publish
        };

        console.log("Update data:", updateData);

        // Update existing post to published state
        const updatedPost = await updatePost(selectedPost.id, updateData);

        console.log("Updated post after update:", updatedPost);

        if (updatedPost) {
          // Send email notification
          console.log("Sending email notification...");
          await handleSendEmailNotification(updatedPost);

          // Auto-sync to Substack if enabled
          try {
            console.log("Attempting auto-sync to Substack...");
            await substackSync.autoSyncOnPublish(updatedPost);
          } catch (syncError) {
            console.error("Substack auto-sync failed:", syncError);
            // Don't block the publish process for sync errors
          }

          setPosts(
            posts.map((post) =>
              post.id === updatedPost.id ? updatedPost : post
            )
          );

          // Update form data to reflect published state
          setFormData({
            ...formData,
            isDraft: false,
          });

          console.log("Post published successfully!");
        } else {
          console.error("Failed to update post");
        }
      } else {
        console.error("No post selected for publishing");
      }
    } catch (error) {
      console.error("Error publishing post:", error);
    } finally {
      setIsLoading(false);
      setShowPublishConfirm(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Process content based on content type
      const processedContent = formData.contentType === 'rich-text' 
        ? formData.content // Rich text content is already HTML
        : await marked(formData.content); // Convert markdown to HTML

      const postData = {
        title: formData.title,
        author: formData.author,
        content: processedContent,
        rawContent: formData.content,
        excerpt: formData.excerpt,
        imageUrl: formData.imageUrl,
        isDraft: formData.isDraft,
        contentType: formData.contentType,
        embeddedImages: formData.embeddedImages,
      };

      if (selectedPost) {
        // Update existing post
        const updatedPost = await updatePost(selectedPost.id, postData);

        if (updatedPost) {
          // We no longer handle publishing here - it's done via the explicit publish button
          setPosts(
            posts.map((post) =>
              post.id === updatedPost.id ? updatedPost : post
            )
          );
        }
      } else {
        // Create new post
        const newPost = await createPost({
          id: uuidv4(),
          ...postData,
          date: new Date().toISOString(),
          likes: 0,
          isDraft: true, // Always create as draft first
        });

        if (newPost) {
          setPosts([...posts, newPost]);
          // Set the newly created post as selected so we can publish it if needed
          setSelectedPost(newPost);
          // Update form data with the new post
          setFormData({
            ...formData,
            isDraft: true,
          });
        }
      }

      // Don't exit editing mode after save
    } catch (error) {
      console.error("Error saving post:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendEmailNotification = async (post: BlogPost) => {
    setIsSendingEmails(true);
    setEmailStatus(null);
    console.log("Starting email notification process for post:", post);

    // Debug post content
    console.log("Post content type:", typeof post.content);
    console.log("Post content length:", post.content ? post.content.length : 0);
    console.log(
      "Post content preview:",
      post.content ? post.content.substring(0, 100) + "..." : "No content"
    );

    try {
      // Generate the post URL - make sure it points to the specific post
      const origin = window.location.origin;
      const isLocalhost =
        origin.includes("localhost") || origin.includes("127.0.0.1");
      const secureOrigin = isLocalhost
        ? origin
        : origin.replace("http:", "https:");
      const postUrl = `${secureOrigin}/post/${post.id}`;

      console.log("Generated post URL:", postUrl);

      // Ensure post content is properly formatted HTML
      const postContent = post.content || "<p>No content available</p>";
      console.log("Sending email with content length:", postContent.length);

      // Send email notification using the new function
      console.log(
        "Calling sendPostNotification with:",
        post.id,
        post.title,
        post.excerpt
      );
      const result = await sendPostNotification(
        post.id,
        post.title,
        post.excerpt || "",
        postUrl,
        postContent // Include the full post content
      );

      console.log("Email notification result:", result);

      if (result.success) {
        setEmailStatus({
          success: true,
          message:
            "Email notification sent to subscribers!" +
            (result.error ? ` Note: ${result.error}` : ""),
        });
      } else {
        setEmailStatus({
          success: false,
          message: `Failed to send email: ${result.error || "Unknown error"}`,
        });
      }
    } catch (error) {
      console.error("Error sending email notification:", error);
      setEmailStatus({
        success: false,
        message: "An unexpected error occurred while sending emails",
      });
    } finally {
      setIsSendingEmails(false);
    }
  };

  const handleDelete = async (postId: string) => {
    if (window.confirm("Are you sure you want to delete this post?")) {
      setIsLoading(true);

      try {
        const success = await deletePost(postId);

        if (success) {
          setPosts(posts.filter((post) => post.id !== postId));
        } else {
          alert("Failed to delete post");
        }
      } catch (error) {
        console.error("Error deleting post:", error);
        alert("An unexpected error occurred while deleting the post");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSelectedPost(null);
  };

  const updatePreview = async (markdownContent: string) => {
    if (markdownContent) {
      try {
        setIsPreviewLoading(true);
        const html = await marked(markdownContent);
        setPreviewHtml(html);
      } catch (error) {
        console.error("Error rendering markdown:", error);
      } finally {
        setIsPreviewLoading(false);
      }
    } else {
      setPreviewHtml("");
    }
  };

  // Update preview when a post is selected or content changes
  useEffect(() => {
    if (formData.content) {
      updatePreview(formData.content);
    }
  }, [selectedPost, formData.content]);

  const handleSendManualNotification = async (post: BlogPost) => {
    if (
      window.confirm(
        `Send email notification for "${post.title}" to all subscribers?`
      )
    ) {
      setSendingEmailPostId(post.id);

      try {
        // Generate the post URL with HTTPS for production
        const origin = window.location.origin;
        const isLocalhost =
          origin.includes("localhost") || origin.includes("127.0.0.1");
        const secureOrigin = isLocalhost
          ? origin
          : origin.replace("http:", "https:");
        const postUrl = `${secureOrigin}/post/${post.id}`;

        // Debug post content
        console.log(
          "Manual notification - Post content type:",
          typeof post.content
        );
        console.log(
          "Manual notification - Post content length:",
          post.content ? post.content.length : 0
        );

        // Ensure post content is properly formatted HTML
        const postContent = post.content || "<p>No content available</p>";
        console.log(
          "Manual notification - Sending email with content length:",
          postContent.length
        );

        // Send the notification
        const result = await sendPostNotification(
          post.id,
          post.title,
          post.excerpt || "",
          postUrl,
          postContent // Include the full post content
        );

        // Show the result
        if (result.success) {
          alert(`Notification sent successfully! ${result.error || ""}`);
        } else {
          alert(
            `Failed to send notification: ${result.error || "Unknown error"}`
          );
        }
      } catch (error) {
        console.error("Error sending manual notification:", error);
        alert("An unexpected error occurred while sending the notification");
      } finally {
        setSendingEmailPostId(null);
      }
    }
  };

  // Show error state
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="text-center py-12">
          <div className="mb-4">
            <svg
              className="w-16 h-16 text-red-500 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {error}
          </h3>
          <button
            onClick={loadPosts}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab("posts")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "posts"
                ? "border-indigo-500 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Posts
          </button>
          <button
            onClick={() => setActiveTab("subscribers")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "subscribers"
                ? "border-indigo-500 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Subscribers
          </button>
          <button
            onClick={() => setActiveTab("substack")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "substack"
                ? "border-indigo-500 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Substack Sync
          </button>
        </nav>
      </div>

      {activeTab === "subscribers" ? (
        <SubscriberManager />
      ) : activeTab === "substack" ? (
        <SubstackSync posts={posts} />
      ) : (
        <div>
          <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2">
          {!isEditing && (
            <div className="relative">
              <input
                type="text"
                placeholder="Search posts..."
                className="px-4 py-2 pr-8 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <svg
                className="w-5 h-5 text-gray-400 absolute right-2 top-2.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          )}

          {!isEditing && (
            <button
              onClick={handleNewPost}
              className="flex items-center gap-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              New Post
            </button>
          )}
        </div>
      </div>

      {isEditing ? (
        <div className="bg-white rounded-lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="title"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Title
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="author"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Author
                </label>
                <input
                  type="text"
                  id="author"
                  name="author"
                  value={formData.author}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="excerpt"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Excerpt
              </label>
              <textarea
                id="excerpt"
                name="excerpt"
                value={formData.excerpt}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                rows={2}
              />
            </div>

            <div>
              <label
                htmlFor="imageUrl"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Image URL
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  id="imageUrl"
                  name="imageUrl"
                  value={formData.imageUrl}
                  onChange={handleInputChange}
                  className="flex-1 px-4 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                  disabled={isUploading}
                >
                  {isUploading ? "Uploading..." : "Upload Image"}
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  className="hidden"
                  accept="image/*"
                />
              </div>
            </div>

            {/* Content Type Selector */}
            <ContentTypeSelector
              contentType={formData.contentType}
              onChange={handleContentTypeChange}
            />

            {/* Content Editor */}
            {formData.contentType === 'markdown' ? (
              <div>
                <label
                  htmlFor="content"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Content (Markdown)
                </label>
                <textarea
                  id="content"
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono"
                  rows={10}
                  required
                  placeholder="Write your post in Markdown format..."
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Content (Rich Text)
                </label>
                <RichTextEditor
                  initialValue={formData.content}
                  onChange={handleRichTextChange}
                  placeholder="Start writing your post..."
                  height={400}
                />
              </div>
            )}

            {/* Preview for both content types */}
            {formData.content && previewHtml && (
              <div className="mt-6 mb-6">
                <div className="flex items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Preview
                  </label>
                  {isPreviewLoading && (
                    <span className="ml-2 text-xs text-gray-500">
                      Updating...
                    </span>
                  )}
                  <span className="ml-2 text-xs text-gray-500">
                    ({formData.contentType === 'rich-text' ? 'Rich Text' : 'Markdown'})
                  </span>
                </div>
                <div
                  className="prose prose-indigo max-w-none p-6 border rounded-md bg-gray-50 overflow-auto max-h-[500px] shadow-inner"
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              </div>
            )}

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="isDraft"
                  checked={formData.isDraft}
                  onChange={handleCheckboxChange}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Save as draft
                </span>
              </label>
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                disabled={isLoading || isUploading || isSendingEmails}
              >
                {isLoading ? "Saving..." : "Save"}
              </button>

              {selectedPost && formData.isDraft && (
                <button
                  type="button"
                  onClick={() => setShowPublishConfirm(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                  disabled={isLoading || isUploading || isSendingEmails}
                >
                  Publish
                </button>
              )}

              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                disabled={isLoading || isUploading || isSendingEmails}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : isLoading ? (
        <LoadingTable rows={5} columns={5} />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="py-3 px-4 text-gray-500 font-medium">Title</th>
                <th className="py-3 px-4 text-gray-500 font-medium">Author</th>
                <th className="py-3 px-4 text-gray-500 font-medium">Date</th>
                <th className="py-3 px-4 text-gray-500 font-medium">
                  Comments
                </th>
                <th className="py-3 px-4 text-gray-500 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {posts.map((post) => (
                <tr key={post.id} className="hover:bg-gray-50">
                  <td className="py-3 px-4 text-gray-800">{post.title}</td>
                  <td className="py-3 px-4 text-gray-600">{post.author}</td>
                  <td className="py-3 px-4 text-gray-600">
                    {new Date(post.date).toLocaleDateString()}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center">
                      <svg
                        className="w-4 h-4 text-gray-400 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                        />
                      </svg>
                      <span className="text-gray-600">
                        {post.comments?.length || 0}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleSelectPost(post)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Edit"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(post.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                      {!post.isDraft && (
                        <button
                          onClick={() => handleSendManualNotification(post)}
                          className="text-green-600 hover:text-green-900"
                          disabled={sendingEmailPostId === post.id}
                          title="Send email notification to subscribers"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-4 text-sm text-gray-500 text-center">
            Showing {posts.length} posts
          </div>
        </div>
      )}

      {/* Publish confirmation dialog */}
      {showPublishConfirm && selectedPost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md">
            <h3 className="text-lg font-semibold mb-4">Publish Post</h3>
            <p className="mb-4">
              Are you sure you want to publish "{selectedPost.title}"? This will
              make it visible to all users and send email notifications to
              subscribers.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowPublishConfirm(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handlePublishPost}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                disabled={isLoading}
              >
                {isLoading ? "Publishing..." : "Publish"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email status notification */}
      {emailStatus && (
        <div
          className={`fixed bottom-4 right-4 p-4 rounded-lg shadow-lg ${
            emailStatus.success
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          <div className="flex items-center">
            <svg
              className={`w-5 h-5 mr-2 ${
                emailStatus.success ? "text-green-500" : "text-red-500"
              }`}
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              {emailStatus.success ? (
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              ) : (
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              )}
            </svg>
            <span>{emailStatus.message}</span>
          </div>
        </div>
      )}
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
